import { defineStore } from 'pinia'

// Constants
const STORAGE_KEY = 'user-state'
const DEFAULT_ITEMS_PER_PAGE = 10

interface UserState {
  id: string | null
  firstname: string | null
  lastname: string | null
  email: string | null
  idToken: string | null
  accessToken: string | null
  itemsPerPage: number
  viewPagination: {
    dashboard: number
    prompt: number
    knowledge: number
  }
  theme: 'light' | 'dark'
  lang: string
  role: string | null
}

export const userStore = defineStore('user', {
  state: (): UserState => {
    const savedState = localStorage.getItem(STORAGE_KEY)
    if (savedState) {
      const parsedState = JSON.parse(savedState)

      // Handle migration to view-specific pagination
      if (!parsedState.viewPagination) {
        const itemsPerPage = parsedState.itemsPerPage || DEFAULT_ITEMS_PER_PAGE
        parsedState.viewPagination = {
          dashboard: itemsPerPage,
          prompt: itemsPerPage,
          knowledge: itemsPerPage
        }
      }

      return parsedState
    }
    return {
      id: null,
      firstname: null,
      lastname: null,
      email: null,
      idToken: null,
      accessToken: null,
      itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
      viewPagination: {
        dashboard: DEFAULT_ITEMS_PER_PAGE,
        prompt: DEFAULT_ITEMS_PER_PAGE,
        knowledge: DEFAULT_ITEMS_PER_PAGE
      },
      theme: 'light',
      lang: 'en',
      role: null
    }
  },
  getters: {
    currentTheme: (state) => state.theme,
    currentLang: (state) => state.lang,
    isDarkTheme: (state) => state.theme === 'dark',
    isAuthenticated: (state) => !!state.accessToken
  },
  actions: {
    setInfo(firstname: string, lastname: string, email: string, idToken: string, accessToken: string) {
      this.firstname = firstname
      this.lastname = lastname
      this.email = email
      this.idToken = idToken
      this.accessToken = accessToken
      this.saveState()
    },
    setItemsPerPage(itemsPerPage: number) {
      this.itemsPerPage = itemsPerPage
      this.saveState()
    },
    setViewItemsPerPage(view: 'dashboard' | 'prompt' | 'knowledge', itemsPerPage: number) {
      this.viewPagination[view] = itemsPerPage
      this.saveState()
    },
    getViewItemsPerPage(view: 'dashboard' | 'prompt' | 'knowledge'): number {
      return this.viewPagination[view]
    },
    setTheme(theme: 'light' | 'dark') {
      this.theme = theme
      this.saveState()
    },
    setLang(lang: string) {
      this.lang = lang
      this.saveState()
    },
    setRole(role: string) {
      this.role = role
      this.saveState()
    },
    setId(id: string) {
      this.id = id
      this.saveState()
    },
    saveState() {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.$state))
    }
  }
})
